import { createFileRoute, Link, useNavigate, redirect } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { SiteLayout } from "@/components/site/site-layout";

export const Route = createFileRoute("/login")({
  head: () => ({
    meta: [
      { title: "Sign in — CoderLity" },
      { name: "description", content: "Sign in to your CoderLity account." },
    ],
  }),
  beforeLoad: async () => {
    const { data } = await supabase.auth.getSession();
    if (data.session) throw redirect({ to: "/" });
  },
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // OTP state
  const [otpEmail, setOtpEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpStage, setOtpStage] = useState<"request" | "verify">("request");
  const [otpLoading, setOtpLoading] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Welcome back!");
    navigate({ to: "/" });
  };

  const requestOtp = async (e: FormEvent) => {
    e.preventDefault();
    setOtpLoading(true);
    const { error } = await supabase.auth.signInWithOtp({
      email: otpEmail,
      options: {
        shouldCreateUser: false,
        emailRedirectTo: `${window.location.origin}/`,
      },
    });
    setOtpLoading(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Check your email for the code or magic link");
    setOtpStage("verify");
  };

  const verifyOtp = async (e: FormEvent) => {
    e.preventDefault();
    if (otpCode.length !== 6) return toast.error("Enter the 6-digit code");
    setOtpLoading(true);
    const { error } = await supabase.auth.verifyOtp({
      email: otpEmail,
      token: otpCode,
      type: "email",
    });
    setOtpLoading(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Signed in!");
    navigate({ to: "/" });
  };

  return (
    <SiteLayout>
      <div className="flex items-center justify-center bg-background px-4 py-16">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Sign in</CardTitle>
            <CardDescription>Welcome back. Choose how you want to continue.</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="password" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="password">Password</TabsTrigger>
                <TabsTrigger value="otp">Email code</TabsTrigger>
              </TabsList>

              <TabsContent value="password" className="mt-4">
                <form onSubmit={onSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? "Signing in..." : "Sign in"}
                  </Button>
                  <div className="text-center">
                    <Link to="/forgot-password" className="text-sm text-muted-foreground hover:text-foreground hover:underline">
                      Forgot password?
                    </Link>
                  </div>
                </form>
              </TabsContent>

              <TabsContent value="otp" className="mt-4">
                {otpStage === "request" ? (
                  <form onSubmit={requestOtp} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="otp-email">Email</Label>
                      <Input
                        id="otp-email"
                        type="email"
                        autoComplete="email"
                        required
                        value={otpEmail}
                        onChange={(e) => setOtpEmail(e.target.value)}
                        placeholder="you@example.com"
                      />
                      <p className="text-xs text-muted-foreground">
                        We'll email you a 6-digit code and a magic link.
                      </p>
                    </div>
                    <Button type="submit" className="w-full" disabled={otpLoading}>
                      {otpLoading ? "Sending..." : "Send code"}
                    </Button>
                  </form>
                ) : (
                  <form onSubmit={verifyOtp} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Enter the code sent to {otpEmail}</Label>
                      <div className="flex justify-center pt-2">
                        <InputOTP maxLength={6} value={otpCode} onChange={setOtpCode}>
                          <InputOTPGroup>
                            <InputOTPSlot index={0} />
                            <InputOTPSlot index={1} />
                            <InputOTPSlot index={2} />
                            <InputOTPSlot index={3} />
                            <InputOTPSlot index={4} />
                            <InputOTPSlot index={5} />
                          </InputOTPGroup>
                        </InputOTP>
                      </div>
                    </div>
                    <Button type="submit" className="w-full" disabled={otpLoading || otpCode.length !== 6}>
                      {otpLoading ? "Verifying..." : "Verify & sign in"}
                    </Button>
                    <div className="flex justify-between text-sm">
                      <button
                        type="button"
                        className="text-muted-foreground hover:text-foreground hover:underline"
                        onClick={() => {
                          setOtpStage("request");
                          setOtpCode("");
                        }}
                      >
                        Use a different email
                      </button>
                      <button
                        type="button"
                        className="text-primary hover:underline"
                        onClick={requestOtp as unknown as () => void}
                      >
                        Resend code
                      </button>
                    </div>
                  </form>
                )}
              </TabsContent>
            </Tabs>

            <p className="mt-4 text-center text-sm text-muted-foreground">
              Don't have an account?{" "}
              <Link to="/signup" className="font-medium text-primary hover:underline">Sign up</Link>
            </p>

          </CardContent>
        </Card>
      </div>
    </SiteLayout>
  );
}
